import React, { useEffect, useRef } from 'react';
import './AnimatedBackground.css';

const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let mouseX = 0;
    let mouseY = 0;
    let isMouseMoving = false;
    let lastMouseMoveTime = 0;

    // Set canvas dimensions
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Create particles
    const createParticles = () => {
      particles = [];
      const particleCount = Math.min(Math.floor(window.innerWidth / 20), 75); // Reduced from 150

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 3 + 1,
          color: getRandomColor(),
          speedX: Math.random() * 1 - 0.5,
          speedY: Math.random() * 1 - 0.5,
          opacity: Math.random() * 0.5 + 0.2
        });
      }
    };

    // Get random color from a palette of matching colors
    const getRandomColor = () => {
      const colors = [
        'rgba(102, 22, 205, 0.8)', // Purple
        'rgba(179, 102, 255, 0.8)', // Light purple
        'rgba(255, 102, 204, 0.8)', // Pink
        'rgba(255, 153, 204, 0.8)', // Light pink
        'rgba(102, 204, 255, 0.8)'  // Light blue
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    };

    // Draw particles
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw gradient background
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width / 2
      );
      gradient.addColorStop(0, 'rgba(102, 22, 205, 0.1)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw connecting lines
      ctx.strokeStyle = 'rgba(102, 22, 205, 0.1)';
      ctx.lineWidth = 0.5;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.globalAlpha = (100 - distance) / 1000;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      particles.forEach(particle => {
        ctx.globalAlpha = particle.opacity;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw heart symbols occasionally
      if (Math.random() < 0.02) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = Math.random() * 20 + 10;
        ctx.globalAlpha = 0.6;
        ctx.font = `${size}px Arial`;
        ctx.fillStyle = 'rgba(255, 102, 204, 0.8)';
        ctx.fillText('❤️', x, y);
      }

      // Draw mouse interaction effect
      if (isMouseMoving) {
        const now = Date.now();
        if (now - lastMouseMoveTime < 2000) {
          ctx.globalAlpha = 0.3;
          const mouseGradient = ctx.createRadialGradient(
            mouseX, mouseY, 0, mouseX, mouseY, 150
          );
          mouseGradient.addColorStop(0, 'rgba(102, 22, 205, 0.3)');
          mouseGradient.addColorStop(1, 'rgba(102, 22, 205, 0)');
          ctx.fillStyle = mouseGradient;
          ctx.beginPath();
          ctx.arc(mouseX, mouseY, 150, 0, Math.PI * 2);
          ctx.fill();
        } else {
          isMouseMoving = false;
        }
      }
    };

    // Update particles
    const updateParticles = () => {
      particles.forEach(particle => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX = -particle.speedX;
        }

        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY = -particle.speedY;
        }

        // Mouse interaction
        if (isMouseMoving) {
          const dx = mouseX - particle.x;
          const dy = mouseY - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const angle = Math.atan2(dy, dx);
            const force = (150 - distance) / 1500;
            particle.speedX += Math.cos(angle) * force;
            particle.speedY += Math.sin(angle) * force;
          }
        }

        // Apply some drag
        particle.speedX *= 0.99;
        particle.speedY *= 0.99;
      });
    };

    // Animation loop
    const animate = () => {
      if (document.hidden) return; // Stop animations when tab is not visible
      updateParticles();
      drawParticles();
      animationFrameId = requestAnimationFrame(animate);
    };

    // Handle mouse movement
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
      isMouseMoving = true;
      lastMouseMoveTime = Date.now();
    };

    // Handle window resize
    const handleResize = () => {
      setCanvasDimensions();
      createParticles();
    };

    // Initialize
    setCanvasDimensions();
    createParticles();
    animate();

    // Add event listeners
    window.addEventListener('resize', handleResize);
    canvas.addEventListener('mousemove', handleMouseMove);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className="animated-bg">
      <canvas ref={canvasRef} className="particle-canvas"></canvas>
      <div className="floating-elements">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="element"
            style={{
              '--delay': `${Math.random() * 5}s`,
              '--size': `${Math.random() * 30 + 20}px`,
              '--x': `${Math.random() * 100}%`,
              '--y': `${Math.random() * 100}%`,
            }}
          >
            {['❤️', '💫', '✨', '💕', '💘'][Math.floor(Math.random() * 5)]}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnimatedBackground;